/**
 * Product-card wishlist toggle.
 *
 * The saved list is an array of product handles kept in localStorage under a single
 * key (`wc_wishlist`). It is per-browser: it is not synced to the customer account,
 * to other devices, or to Shopify. Every storage read and write is wrapped in
 * try/catch so the page keeps working when storage is unavailable (private mode,
 * blocked cookies, quota exceeded) - the button then simply stops persisting.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'wc_wishlist';
  var SELECTOR = '[data-wc-wishlist]';
  var COUNT_SELECTOR = '[data-wc-wishlist-count]';

  /** @returns {string[]} the saved handles, or an empty list if storage is unreadable. */
  function readList() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (value) {
        return typeof value === 'string' && value.length > 0;
      });
    } catch (error) {
      return [];
    }
  }

  /** @returns {boolean} whether the list was persisted. */
  function writeList(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      return true;
    } catch (error) {
      return false;
    }
  }

  function applyState(button, isSaved) {
    var label = isSaved
      ? button.getAttribute('data-label-saved') || 'Saved'
      : button.getAttribute('data-label-save') || 'Save';

    button.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
    button.setAttribute('aria-label', label);
    button.setAttribute('title', label);
  }

  function updateCounts(total) {
    var counters = document.querySelectorAll(COUNT_SELECTOR);
    for (var i = 0; i < counters.length; i++) {
      counters[i].textContent = String(total);
      counters[i].hidden = total === 0;
    }
  }

  /** Restores the saved state of every wishlist button currently in the document. */
  function sync() {
    var list = readList();
    var buttons = document.querySelectorAll(SELECTOR);

    for (var i = 0; i < buttons.length; i++) {
      var handle = buttons[i].getAttribute('data-wc-wishlist-handle');
      applyState(buttons[i], !!handle && list.indexOf(handle) !== -1);
    }

    updateCounts(list.length);
  }

  function toggle(button) {
    var handle = button.getAttribute('data-wc-wishlist-handle');
    if (!handle) return;

    var list = readList();
    var index = list.indexOf(handle);
    var isSaved = index === -1;

    if (isSaved) {
      list.push(handle);
    } else {
      list.splice(index, 1);
    }

    writeList(list);

    // Update every button for this product (a handle can appear in more than one grid).
    var buttons = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < buttons.length; i++) {
      if (buttons[i].getAttribute('data-wc-wishlist-handle') === handle) {
        applyState(buttons[i], isSaved);
      }
    }

    updateCounts(list.length);

    try {
      document.dispatchEvent(
        new CustomEvent('wc:wishlist:change', {
          detail: { handle: handle, saved: isSaved, handles: list.slice() },
        })
      );
    } catch (error) {
      /* CustomEvent is unavailable - the button still works. */
    }
  }

  /*
   * Captured on the document so the toggle runs before the product card's own link
   * and view-transition handlers, and never navigates to the product page.
   */
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var button = target.closest(SELECTOR);
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();
      toggle(button);
    },
    true
  );

  /* Product grids are re-rendered by filtering, sorting and pagination, so re-sync new cards. */
  var pending = false;
  function scheduleSync() {
    if (pending) return;
    pending = true;
    window.requestAnimationFrame(function () {
      pending = false;
      sync();
    });
  }

  function observe() {
    if (typeof window.MutationObserver !== 'function' || !document.body) return;

    new window.MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches(SELECTOR) || node.querySelector(SELECTOR)) {
            scheduleSync();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  function start() {
    sync();
    observe();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', scheduleSync);
  window.addEventListener('pageshow', scheduleSync);
})();
